/**
 * @file PlanYearDropdown.js
 * @description A dropdown component for selecting a plan year.
 */

import React, { useState, useEffect } from 'react';
import { searchPlanYears } from '../../../services/planYearService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

/**
 * Represents a dropdown component for selecting a plan year.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label is displayed inline with the dropdown.
 * @param {string} [props.selectedPlanYear] - The ID of the selected plan year.
 * @param {function} props.onPlanYearChange - The callback function to be called when the selected plan year changes.
 * @param {string} [props.label="Plan Year"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @param {boolean} [props.allowNullSelection=false] - Determines whether a null selection is allowed.
 * @param {boolean} [props.required=false] - Alternative prop for determining if the dropdown is required.
 * @param {number} [props.tenantId] - The tenant ID to filter plan years by.
 * @param {string} [props.sortBy="name"] - The field to sort plan years by.
 * @param {string} [props.sortOrder="asc"] - The sort order for plan years.
 * @param {boolean} [props.returnFullObject=false] - Whether to return the full plan year object in the callback.
 * @param {function} [props.onPlanYearsLoaded] - Called with the fetched plan years (empty on failure) once loading completes.
 * @returns {JSX.Element} The rendered PlanYearDropdown component.
 */
const PlanYearDropdown = ({
    className,
    inlineLabel = true,
    selectedPlanYear,
    onPlanYearChange,
    label = 'Plan Year',
    isRequired = false,
    allowNullSelection = false,
    required = false,
    tenantId = null,
    sortBy = 'name',
    sortOrder = 'asc',
    returnFullObject = false,
    autoSelectCurrent = false,
    onPlanYearsLoaded,
}) => {
    const [planYears, setPlanYears] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getPlanYears = async () => {
            setIsLoading(true);
            let years = [];
            try {
                const data = await searchPlanYears(
                    tenantId,
                    1,
                    1000,
                    '',
                    sortBy,
                    sortOrder,
                );
                years = data.viewModels ?? [];
                setPlanYears(years);

                if (
                    autoSelectCurrent &&
                    !selectedPlanYear &&
                    years.length > 0
                ) {
                    const todayMs = new Date().getTime();
                    const current = years.find(y => {
                        const start = y.startDate
                            ? new Date(y.startDate).getTime()
                            : -Infinity;
                        const end = y.endDate
                            ? new Date(y.endDate).getTime()
                            : Infinity;
                        return todayMs >= start && todayMs <= end;
                    });
                    const fallback = [...years].sort((a, b) =>
                        String(b.startDate || '').localeCompare(
                            a.startDate || '',
                        ),
                    )[0];
                    const defaultYear = current ?? fallback;
                    if (defaultYear) {
                        if (returnFullObject) {
                            onPlanYearChange(defaultYear.id, defaultYear);
                        } else {
                            onPlanYearChange(defaultYear.id);
                        }
                    }
                }
            } catch (error) {
                console.error('Could not fetch plan years:', error);
                Toast.error(getText('msg_f05dc209333c'));
            } finally {
                setIsLoading(false);
                onPlanYearsLoaded?.(years);
            }
        };

        getPlanYears();
    }, [tenantId, sortBy, sortOrder]);

    const handlePlanYearSelection = planYearId => {
        if (returnFullObject && planYearId) {
            const selectedPlanYearObject = planYears.find(
                planYear => planYear.id === planYearId,
            );
            onPlanYearChange(planYearId, selectedPlanYearObject);
        } else {
            onPlanYearChange(planYearId);
        }
    };

    const getDefaultLabel = () => {
        const matchingPlanYear = planYears.find(
            planYear => planYear.id === selectedPlanYear,
        );
        return matchingPlanYear ? matchingPlanYear.name : 'Select Plan Year';
    };

    const planYearOptions = allowNullSelection
        ? [
              { value: null, label: getText('msg_28670ab4dbce') },
              ...planYears.map(planYear => ({
                  value: planYear.id,
                  label: planYear.name,
              })),
          ]
        : planYears.map(planYear => ({
              value: planYear.id,
              label: planYear.name,
          }));

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <label htmlFor="plan-year-dropdown">
                {label}
                {(isRequired || required) && (
                    <span className="text-danger">*</span>
                )}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    id="plan-year-dropdown"
                    defaultValue={getDefaultLabel()}
                    options={planYearOptions}
                    value={selectedPlanYear}
                    onChange={handlePlanYearSelection}
                    disabled={planYears.length === 0 || isLoading}
                />
            </LoadingWrapper>
        </div>
    );
};

export default PlanYearDropdown;
